import { memo, useEffect, type FC } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Loader } from '../../Elements/Loader/index.tsx';
import { useScrollNavigationContext } from '../../hooks/useScrollNavigation/index.ts';
import { Item } from './components/index.ts';
import { LoaderWrapper, Wrapper } from './styled.ts';

export interface HorizontalNavigationGroupProps {
  isLoading?: boolean;
  persistKey?: string;
}

export const HorizontalNavigationGroup: FC<HorizontalNavigationGroupProps> = memo(function HorizontalNavigationGroup({
  isLoading,
  persistKey,
}) {
  const { activeId, scrollToSection, sections } = useScrollNavigationContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const handleScrollToItem = useCallbackRef((id: string) => {
    if (persistKey) {
      setSearchParams((prev) => {
        prev.set(persistKey, id);
        return prev;
      });
    }

    scrollToSection(id);
  });

  useEffect(() => {
    if (!persistKey) return;
    const activeTabId = searchParams.get(persistKey);
    if (activeTabId && sections.some((section) => section.id === activeTabId)) {
      scrollToSection(activeTabId, { behavior: 'instant' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  return (
    <Wrapper>
      {isLoading && (
        <LoaderWrapper>
          <Loader height={44} />
        </LoaderWrapper>
      )}
      {!isLoading &&
        sections.map(({ id, title, dataTestId }) => (
          <Item key={id} id={id} title={title} active={id === activeId} setActiveId={handleScrollToItem} dataTestId={dataTestId} />
        ))}
    </Wrapper>
  );
});
